/**
 * PATH: packages/job-hub/src/jobs/tabs/JobQaModeToggle.tsx
 * FILE_ID: JOBHUB-JOBS-QA-MODE-TOGGLE-001
 * PURPOSE: Build/Run/ITP radiogroup for the QA checklist. Build edits,
 *          Run executes, ITP reviews and prints the plan.
 * WHY:     Extracted VERBATIM from JobQaTab.tsx during the frame decompose.
 * RELATED:
 * - JOBHUB-JOBS-TAB-QA-001 (the frame that renders this)
 */
import type { ReactNode } from 'react'
import { FileCheck2, ListChecks, Save, Table2 } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'

export type QaAreaMode = 'build' | 'run' | 'itp'

const MODE_OPTIONS = [
  ['build', 'Build', Table2, 'Edit the checklist like a spreadsheet — rename cells, add/move/delete rows, resize columns, right-click for more'],
  ['run', 'Run', ListChecks, 'Execute the checklist on site — tick items, capture photos and readings'],
  ['itp', 'ITP', FileCheck2, 'Inspection Test Plan - review, print, or download the plan for this location'],
] as const

export function JobQaModeToggle({
  mode,
  onChange,
  actions,
  showBuild = true,
  onSaveToLibrary,
}: {
  mode: QaAreaMode
  onChange: (mode: QaAreaMode) => void
  actions?: ReactNode
  showBuild?: boolean
  onSaveToLibrary?: () => void
}) {
  const modes = showBuild ? MODE_OPTIONS : MODE_OPTIONS.filter(([value]) => value !== 'build')
  return (
    <div className="-mx-1 mb-0 flex min-w-0 flex-wrap items-center gap-2 px-1 py-0.5">
      {actions}
      <div
        role="radiogroup"
        aria-label="Checklist mode"
        className="ml-auto inline-flex rounded-lg border border-border p-0.5"
      >
        {modes.map(([value, label, Icon, hint]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            title={hint}
            onClick={() => onChange(value)}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
              mode === value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {label}
          </button>
        ))}
      </div>
      {onSaveToLibrary ? (
        <button
          type="button"
          onClick={onSaveToLibrary}
          title="Save to ITP library"
          aria-label="Save to ITP library"
          data-testid="qa-itp-library-save"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-foreground hover:bg-accent"
        >
          <Save className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
